import {
  Breakpoint,
  PalmettoBottomSheet,
  PalmettoBottomSheetProps,
  PalmettoModal,
  PalmettoModalProps,
  useBreakpoint,
} from '@palmetto/dsm-components';
import { Themes } from '@palmetto/dsm-tokens';
import { ReactNode } from 'react';
import { useAppContainerRef } from '../../hooks/useAppContainerRef';
import useComponentTracking from '../../hooks/useComponentTracking';
import { trackEvent } from '../../tracking/Tracking';

export interface ModalV3Props {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  trackingId: string;
  showCloseButton?: boolean;
  desktopCta?: ReactNode;
  mobileCta?: ReactNode;
  cta?: ReactNode;
  theme?: Themes;
  bottomSheetProps?: Partial<PalmettoBottomSheetProps>;
  modalProps?: Partial<PalmettoModalProps>;
}

const SMALL_BREAKPOINTS: Set<Breakpoint> = new Set(['xs', 'sm']);

export const ModalV3 = (props: ModalV3Props) => {
  const {
    open,
    onClose,
    children,
    trackingId,
    showCloseButton = true,
    desktopCta,
    mobileCta,
    cta,
    theme,
    bottomSheetProps,
    modalProps,
  } = props;
  const { currentBreakpoint } = useBreakpoint();
  const appContainerRef = useAppContainerRef();

  const isSmallBreakpoint = SMALL_BREAKPOINTS.has(currentBreakpoint);

  const handleClose = () => {
    trackEvent('modal closed', { trackingId });
    onClose();
  };

  useComponentTracking(trackingId, !open);

  if (isSmallBreakpoint) {
    return (
      <PalmettoBottomSheet
        open={open}
        onClose={handleClose}
        container={appContainerRef}
        bottomContent={cta || mobileCta}
        theme={theme}
        showCloseButton={showCloseButton}
        {...bottomSheetProps}
      >
        {children}
      </PalmettoBottomSheet>
    );
  }

  const desktopBottomContent = cta || desktopCta;

  return (
    <PalmettoModal
      invertedBackground={false}
      open={open}
      onClose={handleClose}
      showClose={showCloseButton}
      container={appContainerRef}
      data-theme={theme}
      bottomContent={desktopBottomContent}
      {...modalProps}
    >
      {children}
    </PalmettoModal>
  );
};
